import {
  Text,
  GreyWhite,
  GreyGrey30,
  Primary14SemiBold,
  Primary13Regular,
  GreyGrey80,
  GreyGrey70,
  Flex
} from '@sequencehq/core-components'
import {
  CreditNoteStatusBadge,
  CreditNoteDetailsHeaderActions
} from 'CreditNotes/components'
import {
  CreditNoteApplicationStatus,
  CreditNoteStatus,
  LineItem
} from 'CreditNotes/types'
import { match, P } from 'ts-pattern'
import useToggle from 'lib/hooks/useToggle.tsx'
import { PdfPreviewDrawer } from 'CreditNotes/components/drawers/PdfPreviewDrawer'
import { PageTitle } from '@sequencehq/core-components'

interface Props {
  creditNoteNumber: string | undefined
  invoiceNumber: string | undefined
  creditNoteId: string
  status: CreditNoteStatus
  applicationStatus: CreditNoteApplicationStatus | undefined
  incompleteLineItems: LineItem[]
  hasNegativeTotal: boolean
  onEditLineItem: (id: string) => void
  onTriggerValidation: (triggered: boolean) => void
}

export const CreditNoteDetailsHeader = ({
  creditNoteNumber,
  creditNoteId,
  invoiceNumber,
  status,
  applicationStatus,
  incompleteLineItems,
  hasNegativeTotal,
  onEditLineItem,
  onTriggerValidation
}: Props) => {
  const [showPDFPreview, toggleShowPDFPreview] = useToggle(false)
  const pageTitle =
    creditNoteNumber ??
    (invoiceNumber ? 'Issue a credit note' : 'Draft credit note')

  return (
    <>
      <PageTitle title={[pageTitle, 'Credit notes']} />
      <Flex
        height="48px"
        padding={4}
        backgroundColor={GreyWhite}
        borderBottom={`1px solid ${GreyGrey30}`}
        justifyContent="space-between"
        alignItems="center"
        alignContent="center"
      >
        <Flex
          alignItems="center"
          alignContent="center"
          gap="4px"
          data-testid="creditNote.header"
        >
          <CreditNoteTitle
            creditNoteNumber={creditNoteNumber}
            invoiceNumber={invoiceNumber}
          />
          <CreditNoteStatusBadge
            status={status}
            applicationStatus={applicationStatus}
          />
        </Flex>

        <CreditNoteDetailsHeaderActions
          creditNoteId={creditNoteId}
          creditNoteNumber={creditNoteNumber}
          creditNoteStatus={status}
          onPreviewCreditNotePDF={() => toggleShowPDFPreview(true)}
          incompleteLineItems={incompleteLineItems}
          hasNegativeTotal={hasNegativeTotal}
          onEditLineItem={onEditLineItem}
          onTriggerValidation={onTriggerValidation}
        />
      </Flex>
      {showPDFPreview && (
        <PdfPreviewDrawer
          closeModal={() => toggleShowPDFPreview(false)}
          creditNoteId={creditNoteId}
          creditNoteStatus={status}
          creditNoteNumber={creditNoteNumber}
        />
      )}
    </>
  )
}

const CreditNoteTitle = ({
  creditNoteNumber,
  invoiceNumber
}: {
  creditNoteNumber?: string
  invoiceNumber?: string
}) => {
  return match({ creditNoteNumber, invoiceNumber })
    .with({ creditNoteNumber: undefined, invoiceNumber: undefined }, () => (
      <Text {...Primary14SemiBold} color={GreyGrey80}>
        Issue a credit note
      </Text>
    ))
    .with({ creditNoteNumber: P._, invoiceNumber: undefined }, () => (
      <Text {...Primary14SemiBold} color={GreyGrey80}>
        {creditNoteNumber}
      </Text>
    ))
    .with({ creditNoteNumber: undefined, invoiceNumber: P._ }, () => (
      <Flex alignItems="center" alignContent="center" gap="4px">
        <Text {...Primary14SemiBold} color={GreyGrey80}>
          Issue a credit note
        </Text>
        <Text {...Primary13Regular} color={GreyGrey70}>
          for {invoiceNumber}
        </Text>
      </Flex>
    ))
    .with({ creditNoteNumber: P._, invoiceNumber: P._ }, () => (
      <Flex alignItems="center" alignContent="center" gap="4px">
        <Text {...Primary14SemiBold} color={GreyGrey80}>
          {creditNoteNumber}
        </Text>
        <Text {...Primary13Regular} color={GreyGrey70}>
          for {invoiceNumber}
        </Text>
      </Flex>
    ))
    .exhaustive()
}
